import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    Field,
    FieldDescription,
    FieldLabel,
    FieldTitle
} from "@solomon-labs/design/components/ui";
import type { ReactElement, ReactNode } from "react";

/**
 * Single-field wrapper used by every structured onboarding form for its
 * top-level fields: a title, an optional hint, and the control itself.
 *
 * Built on the design system's `Field` family rather than a hand-rolled
 * stack. That is what supplies the parts this page was faking:
 *   - `FieldLabel` renders a real `<label htmlFor>`, so the title focuses
 *     its control on click. The page previously had zero `<label>` elements
 *     and leaned on `aria-label`, which gives screen readers a name but
 *     gives a pointer nothing.
 *   - `FieldDescription` is the hint's semantic slot.
 * `FieldLabel` bakes in layout only, no typography, so the h3 title survives
 * the move — the `Text` variant below still owns the type.
 *
 * Pass `htmlFor` whenever the field wraps ONE focusable control. Omit it for
 * composite controls (a ToggleGroup, a chip row): a `<label>` pointing at a
 * group is a lie, so those get `FieldTitle`, which is a plain div.
 *
 * Deliberately NOT a `Card`, and it carries no rule. Elevation marks what is
 * actionable (`ui-guide.md` 1a/5a) and rules belong to the section headings;
 * one per field made a seven-field form read as seven sections. Separation
 * here is space alone — `pt-6` per field against the `pt-10` that opens a
 * section.
 */
export function FormCard({
    title,
    hint,
    htmlFor,
    children
}: {
    title: string;
    hint?: string;
    /** Id of the single control this field labels. Omit for composite
     *  controls — see the note above. */
    htmlFor?: string;
    children: ReactNode;
}): ReactElement {
    const heading = (
        <Text variant="h3" render={<span />}>
            {title}
        </Text>
    );

    return (
        <Field orientation="vertical" className="pt-6">
            {htmlFor ? (
                <FieldLabel htmlFor={htmlFor}>{heading}</FieldLabel>
            ) : (
                <FieldTitle>{heading}</FieldTitle>
            )}
            {hint ? <FieldDescription>{hint}</FieldDescription> : null}
            {children}
        </Field>
    );
}
